import React, { useEffect, useState, ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  getDocumentsByType,
  deleteDocument,
  getPresignedUpload,
  confirmUpload,
  createDocument,
  ApplicationDocumentDto,
} from "../../api/services";

interface DocumentCardProps {
  applicationId: string;
  documentType: string;
  titleKey: string;
  inputLabelKey: string;
  colFileNameKey: string;
  colActionsKey: string;
  deleteLabelKey: string;
  confirmDeleteKey: string;
  errorFetchingKey: string;
  errorDeletingKey: string;
  errorUploadingKey: string;
}

export const DocumentCard: React.FC<DocumentCardProps> = ({
  applicationId,
  documentType,
  titleKey,
  inputLabelKey,
  colFileNameKey,
  colActionsKey,
  deleteLabelKey,
  confirmDeleteKey,
  errorFetchingKey,
  errorDeletingKey,
  errorUploadingKey,
}) => {
  const { t } = useTranslation();

  const [documents, setDocuments] = useState<ApplicationDocumentDto[]>([]);
  const [loadingDocs, setLoadingDocs] = useState<boolean>(true);
  const [docsError, setDocsError] = useState<string | null>(null);

  const [uploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const fetchDocuments = () => {
    setLoadingDocs(true);
    setDocsError(null);

    getDocumentsByType(applicationId, documentType)
      .then((res) => setDocuments(res.data))
      .catch((err) => {
        console.error(err);
        setDocsError(t(errorFetchingKey));
      })
      .finally(() => setLoadingDocs(false));
  };

  useEffect(() => {
    fetchDocuments();
  }, [applicationId, documentType, t]);

  const handleDelete = (docId: number) => {
    if (!window.confirm(t(confirmDeleteKey))) return;
    deleteDocument(applicationId, docId)
      .then(() => fetchDocuments())
      .catch((err) => {
        console.error(err);
        setUploadError(t(errorDeletingKey));
      });
  };

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    setUploadError(null);
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const presignedResp = await getPresignedUpload(file.name);
      const { uuid, presignedUrl } = presignedResp.data;

      await fetch(presignedUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      await confirmUpload(uuid);
      await createDocument(applicationId, {
        type: documentType,
        uploadUuid: uuid,
      });

      fetchDocuments();
    } catch (err) {
      console.error(err);
      setUploadError(t(errorUploadingKey));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="mb-8">
      <h2 className="mb-4">{t(titleKey)}</h2>
      <div className="rounded-xl bg-white shadow-sm">
        <div className="p-5">
          {loadingDocs ? (
            <p>{t(`${titleKey}.loading`, "Loading…")}</p>
          ) : docsError ? (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{docsError}</div>
          ) : (
            <>
              {documents.length > 0 && (
                <table className="w-full text-sm mb-6">
                  <tbody>
                    {documents.map((doc) => (
                      <tr key={doc.id}>
                        <td className="px-4 py-3">
                          <strong>{doc.file.fileName}</strong>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            className="rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
                            onClick={() => handleDelete(doc.id)}
                          >
                            {t(deleteLabelKey)}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <form>
                <div className="mb-4">
                  <label
                    htmlFor={`${documentType}-input`}
                    className="mb-1.5 block text-sm font-medium text-gray-700"
                  >
                    {t(inputLabelKey)}
                  </label>
                  <input
                    type="file"
                    id={`${documentType}-input`}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-sm transition-all focus:border-primary focus:ring-2 focus:ring-primary/20 focus:outline-none"
                    accept="application/pdf,image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                  />
                </div>

                {uploadError && (
                  <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">{uploadError}</div>
                )}

                {uploading && (
                  <div className="text-info">
                    {t(`${titleKey}.uploading`, "Uploading…")}
                  </div>
                )}
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
